import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { api } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";
import { HttpError } from "@/utils/permissions";

import type { RequestRequest } from ".";
import { type RequestFlow } from "../types";

export type RequestFlowList = Paginate<RequestFlow<true, true, true, true>>;

export const requestFlows = createQueryKeys("application-request-flows", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
  oneByUuid: (uuid?: string) => [uuid],
});

const include =
  "applicationVersion.flows.children,applicationVersion.builder,request.user,request.requestFlows";

export const fetchApplicationRequestFlowsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...requestFlows.all(page, search),
    queryFn: () =>
      api
        .get<RequestFlowList>(`application/request-flows/${status}`, {
          params: {
            include:
              "applicationVersion.flows.children,request.user,request.requestFlows",
            page,
            ...search,
          },
        })
        .then((response) => response.data),
  });

export const useFetchApplicationRequestFlows = (search?: SearchRequest) =>
  usePaginateQuery((page) =>
    fetchApplicationRequestFlowsQueryOptions(page, search),
  );

export const fetchApplicationRequestFlowQueryOptions = (
  id: number | undefined,
) =>
  queryOptions({
    ...requestFlows.one(id),
    queryFn: () =>
      api
        .get<
          RequestFlow<true, true, true, true, true>
        >(`application/request-flows/${id}?include=${include}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchApplicationRequestFlow = (id: number | undefined) =>
  useQuery(fetchApplicationRequestFlowQueryOptions(id));

export const fetchApplicationRequestFlowByUuidQueryOptions = (
  uuid: string | undefined,
) =>
  queryOptions({
    ...requestFlows.oneByUuid(uuid),
    queryFn: async () => {
      try {
        const response = await api.get<
          RequestFlow<true, true, true, true, true>
        >(`application/request-flows/uuid/${uuid}?include=${include}`);
        return response.data;
      } catch (error: unknown) {
        if (
          isAxiosError(error) &&
          error.response &&
          [403, 404].includes(error.response.status)
        ) {
          throw new HttpError(403, "Access Denied");
        }

        throw error;
      }
    },
    enabled: uuid !== undefined,
    meta: { hideError: true },
  });

export const useFetchApplicationRequestFlowByUuid = (
  uuid: string | undefined,
) => useQuery(fetchApplicationRequestFlowByUuidQueryOptions(uuid));

export interface RequestFlowRequest extends RequestRequest {
  status: "approved" | "rejected";
  description?: string;
}

export const useUpdateApplicationRequestFlow = (
  applicationRequestFlowId: number,
) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: RequestFlowRequest) =>
      api
        .put<
          RequestFlow<true, true, true, true, true>
        >(`application/request-flows/${applicationRequestFlowId}?include=${include}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: requestFlows.all._def,
      });
    },
  });
};

export const useUpdateApplicationRequestFlowByUuid = (uuid: string) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: RequestFlowRequest) =>
      api
        .put<
          RequestFlow<true, true, true, true, true>
        >(`application/request-flows/uuid/${uuid}?include=${include}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: requestFlows.all._def,
      });
    },
  });
};

export const useCancelApplicationRequestFlow = (
  applicationRequestFlowId: number,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ description }: { description: string }) =>
      api
        .post(`application/request-flows/${applicationRequestFlowId}/cancel`, {
          description,
        })
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: requestFlows.all._def,
      });
    },
  });
};
